import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useRequiredContext } from '@plato/ui-legacy/lib/hooks/use-required-context';
import type { QueryParamsObjectConfig } from '@plato/ui-legacy/lib/query-params';
import {
    enumParam,
    generateQueryParamsString,
    numberParam,
    objectParam,
    requiredParam,
    stringArrayParam,
    stringParam,
    transientParam,
} from '@plato/ui-legacy/lib/query-params';
import React from 'react';
import { usePersistedQueryParams } from '../../../lib/hooks/use-persisted-query-params.ts';
import type {
    CustomDateRange,
    ExplorerSort,
    ExplorerViewFilters,
} from '../../../server/utils/explorer-utils.ts';
import {
    BillingStatus,
    ExplorerSortBy,
    ExplorerSortOrder,
    ExplorerTopLevelEntity,
    TargetUnit,
    VisibilityWindow,
    resolveActiveMetric,
} from '../../../server/utils/explorer-utils.ts';
import { ExplorerViewContext } from './explorer-view-context.ts';

export type ExplorerState = {
    tab: ExplorerTopLevelEntity;
    searchString: string;
    searchStringCustomer: string;
    searchStringClerk: string;
    searchStringIndustry: string;
    searchStringProduct: string;
    searchStringProductGroup: string;
    searchStringStorageLocation: string;
    searchStringSupplier: string;
    searchStringSalesRep: string;
    searchStringNormalisedCategory: string;
    window: VisibilityWindow;
    billing: BillingStatus;
    targetUnit: TargetUnit;
    sorting: NonNullable<ExplorerSort>;
    customDateRange: CustomDateRange | undefined;
    yearPeriods: number;
} & Required<ExplorerViewFilters>;

const explorerQueryParamsConfig = {
    tab: enumParam(ExplorerTopLevelEntity),
    searchString: transientParam(stringParam()),
    searchStringCustomer: transientParam(stringParam()),
    searchStringClerk: transientParam(stringParam()),
    searchStringIndustry: transientParam(stringParam()),
    searchStringProduct: transientParam(stringParam()),
    searchStringProductGroup: transientParam(stringParam()),
    searchStringStorageLocation: transientParam(stringParam()),
    searchStringSupplier: transientParam(stringParam()),
    searchStringSalesRep: transientParam(stringParam()),
    searchStringNormalisedCategory: transientParam(stringParam()),
    window: enumParam(VisibilityWindow),
    customDateRange: objectParam<CustomDateRange>({
        from: requiredParam(stringParam()),
        to: requiredParam(stringParam()),
    }),
    billing: enumParam(BillingStatus),
    targetUnit: enumParam(TargetUnit),
    sorting: objectParam<NonNullable<ExplorerSort>>({
        field: requiredParam(enumParam(ExplorerSortBy)),
        direction: requiredParam(enumParam(ExplorerSortOrder)),
    }),
    yearPeriods: numberParam(),
    // filters
    customer: stringArrayParam(),
    clerk: stringArrayParam(),
    industry: stringArrayParam(),
    product: stringArrayParam(),
    productGroup: stringArrayParam(),
    storageLocation: stringArrayParam(),
    supplier: stringArrayParam(),
    salesRep: stringArrayParam(),
    normalisedCategory: stringArrayParam(),
} satisfies QueryParamsObjectConfig<ExplorerState>;

export const defaultExplorerFilters: ExplorerViewFilters = {
    customer: [],
    clerk: [],
    industry: [],
    product: [],
    productGroup: [],
    storageLocation: [],
    supplier: [],
    salesRep: [],
    normalisedCategory: [],
};

/** Every tab owns its own search key; they are cleared together on tab change and clear-all. */
type ExplorerSearchKey = Extract<keyof ExplorerState, `searchString${string}`>;

export const clearedExplorerSearches = {
    searchString: '',
    searchStringCustomer: '',
    searchStringClerk: '',
    searchStringIndustry: '',
    searchStringProduct: '',
    searchStringProductGroup: '',
    searchStringStorageLocation: '',
    searchStringSupplier: '',
    searchStringSalesRep: '',
    searchStringNormalisedCategory: '',
} satisfies Record<ExplorerSearchKey, string>;

export const initialExplorerState: Omit<ExplorerState, 'customDateRange'> = {
    ...defaultExplorerFilters,
    ...clearedExplorerSearches,
    tab: ExplorerTopLevelEntity.STORAGE_LOCATION,
    window: VisibilityWindow.YTD,
    billing: BillingStatus.BILLED,
    targetUnit: TargetUnit.REVENUE,
    sorting: {
        field: ExplorerSortBy.REVENUE_CURRENT,
        direction: ExplorerSortOrder.DESC,
    },
    yearPeriods: 2,
};

export function generateSearchParamsForExplorer(params: Partial<ExplorerState>) {
    return generateQueryParamsString(explorerQueryParamsConfig, params);
}

// Helper function to get the search string field name for a tab
export function getSearchStringFieldForTab(tab: ExplorerTopLevelEntity): keyof ExplorerState {
    switch (tab) {
        case ExplorerTopLevelEntity.CUSTOMER:
            return 'searchStringCustomer';
        case ExplorerTopLevelEntity.CLERK:
            return 'searchStringClerk';
        case ExplorerTopLevelEntity.INDUSTRY:
            return 'searchStringIndustry';
        case ExplorerTopLevelEntity.PRODUCT:
            return 'searchStringProduct';
        case ExplorerTopLevelEntity.PRODUCT_GROUP:
            return 'searchStringProductGroup';
        case ExplorerTopLevelEntity.STORAGE_LOCATION:
            return 'searchStringStorageLocation';
        case ExplorerTopLevelEntity.SUPPLIER:
            return 'searchStringSupplier';
        case ExplorerTopLevelEntity.SALES_REP:
            return 'searchStringSalesRep';
        case ExplorerTopLevelEntity.NORMALISED_CATEGORY:
            return 'searchStringNormalisedCategory';
        default:
            return 'searchString';
    }
}

// Helper function to get the search string value for the current tab
export function getSearchStringForTab(state: Partial<ExplorerState>, tab: ExplorerTopLevelEntity): string {
    const fieldName = getSearchStringFieldForTab(tab);
    return (state[fieldName] as string) || '';
}

/**
 * `initialExplorerState` with the metric and timeframe the tenant reports in. Behind
 * `enableDefaultMetricAndTimeframe` the two dropdowns start on `defaultMetric` /
 * `defaultTimeframe` instead of the hardcoded Revenue / YTD pair; with the flag off both
 * resolve back to those hardcoded values, so nothing moves until a tenant is switched on.
 *
 * Only a default: a URL param or the user's own persisted pick still wins.
 */
function useInitialExplorerState(): typeof initialExplorerState {
    const appConfig = useAppConfig();
    const { billingStatus } = resolveActiveMetric(appConfig);

    return {
        ...initialExplorerState,
        billing: billingStatus,
        window: appConfig.enableDefaultMetricAndTimeframe
            ? appConfig.defaultTimeframe
            : initialExplorerState.window,
    };
}

type UseExplorerQueryParamsProps = {
    tab?: ExplorerTopLevelEntity;
} & Partial<ExplorerViewFilters>;

export function useExplorerQueryParams(
    { tab = ExplorerTopLevelEntity.STORAGE_LOCATION, ...defaultFilters }: UseExplorerQueryParamsProps,
    storeKey?: string,
) {
    const { enableDefaultMetricAndTimeframe } = useAppConfig();
    const initialState = useInitialExplorerState();

    // The tenant default has to outrank whatever metric and window this browser last
    // stored, or a returning user would never meet it. Transient keys stay out of
    // localStorage, so every visit starts from the default; a URL param still wins.
    const config = React.useMemo(
        () =>
            enableDefaultMetricAndTimeframe
                ? {
                      ...explorerQueryParamsConfig,
                      window: transientParam(enumParam(VisibilityWindow)),
                      billing: transientParam(enumParam(BillingStatus)),
                  }
                : explorerQueryParamsConfig,
        [enableDefaultMetricAndTimeframe],
    );

    const persisted = usePersistedQueryParams(
        config,
        {
            ...initialState,
            ...defaultFilters,
            tab,
        },
        storeKey ?? 'explorer',
    );

    // Changing the default from the settings menu has to move the report already on
    // screen.
    const [, setState] = persisted;
    const { billing, window: timeframe } = initialState;
    const appliedDefaults = React.useRef({ billing, window: timeframe });

    React.useEffect(() => {
        if (!enableDefaultMetricAndTimeframe) {
            return;
        }

        // Mount applied these as the defaults already; only a later change is news.
        if (appliedDefaults.current.billing === billing && appliedDefaults.current.window === timeframe) {
            return;
        }

        appliedDefaults.current = { billing, window: timeframe };
        setState((previousState) => ({ ...previousState, billing, window: timeframe }));
    }, [enableDefaultMetricAndTimeframe, billing, timeframe, setState]);

    return persisted;
}

export function useExplorerQueryFilters(props: UseExplorerQueryParamsProps) {
    const [state] = useExplorerQueryParams(props);
    return getExplorerQueryFilters(state);
}

function getExplorerQueryFilters(state: Partial<ExplorerState>): ExplorerViewFilters {
    return {
        customer: state.customer ?? [],
        clerk: state.clerk ?? [],
        industry: state.industry ?? [],
        product: state.product ?? [],
        productGroup: state.productGroup ?? [],
        storageLocation: state.storageLocation ?? [],
        supplier: state.supplier ?? [],
        salesRep: state.salesRep ?? [],
        normalisedCategory: state.normalisedCategory ?? [],
    };
}

export const ExplorerViewProvider = ExplorerViewContext.Provider;

export function useExplorerView() {
    const context = useRequiredContext(ExplorerViewContext, 'ExplorerViewProvider');
    const initialState = useInitialExplorerState();

    const state = {
        ...initialState,
        ...context.state,
    };

    const filters = getExplorerQueryFilters(state);

    const combinedFilters = {
        ...filters,
        ...context.defaultFilters,
    };

    return {
        ...context,
        state: {
            ...state,
            billingStatus: state.billing,
            searchString: getSearchStringForTab(state, state.tab),
        },
        /**
         * These are the filters that are applied by the user
         */
        filters,
        /**
         * These are the filters that will have the default values applied
         */
        combinedFilters,
    };
}
